import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ComicCard from "@/components/feature/ComicCard";
import SchemaOrg from "@/components/feature/SchemaOrg";
import { getComicById, getRelatedComics } from "@/mocks/comics";

export default function ComicDetailPage() {
  const { id } = useParams<{ id: string }>();
  const comic = getComicById(Number(id));

  if (!comic) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
              <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-bold text-foreground-950 mb-2">漫画未找到</h1>
            <p className="text-foreground-600 mb-4">抱歉，您要找的漫画不存在或已被下架</p>
            <Link to="/" className="px-5 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 cursor-pointer transition-colors whitespace-nowrap">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedComics = getRelatedComics(comic);

  const formatViews = (v: number) => {
    if (v >= 10000) return (v / 10000).toFixed(1) + "万";
    return v.toString();
  };

  return (
    <>
      <SchemaOrg
        type="WebPage"
        data={{
          name: `${comic.title} - 番茄漫画`,
          url: `https://fanqiecomic.com/comic/${comic.id}`,
          description: `在线阅读${comic.title}，作者${comic.author}，${comic.status}，共${comic.chapters}话。番茄漫画提供${comic.title}高清免费在线阅读，支持离线下载缓存。`,
        }}
      />
      <Navbar />
      <main className="pt-16">
        <Breadcrumb
          items={[
            { name: "首页", path: "/" },
            { name: comic.region === "korean" ? "韩漫" : comic.region === "japanese" ? "日漫" : "国漫", path: `/category/${comic.region}` },
            { name: comic.title },
          ]}
        />

        <section className="w-full px-4 md:px-6 py-6 md:py-10 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-8">
              <div className="w-full lg:w-[280px] flex-shrink-0">
                <div className="aspect-[3/4] rounded-lg overflow-hidden bg-background-100 sticky top-24" data-product-shop="">
                  <img
                    src={comic.cover}
                    alt={comic.title + " - 番茄漫画免费在线阅读"}
                    title={comic.title + " - 番茄漫画"}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-3">
                  <span className={`text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${
                    comic.status === "完结" ? "bg-secondary-100 text-secondary-800" : "bg-primary-100 text-primary-700"
                  }`}>
                    {comic.status}
                  </span>
                  <span className="text-xs text-foreground-500 whitespace-nowrap">{comic.region === "korean" ? "韩漫" : comic.region === "japanese" ? "日漫" : "国漫"}</span>
                </div>
                <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-2">
                  {comic.title}
                </h1>
                <p className="text-sm text-foreground-600 mb-4">
                  作者：<strong>{comic.author}</strong>
                </p>

                <div className="flex items-center gap-6 mb-6">
                  <div className="flex items-center gap-1">
                    <i className="ri-star-fill text-yellow-400"></i>
                    <span className="text-sm font-semibold text-foreground-900">{comic.rating}</span>
                  </div>
                  <div className="text-sm text-foreground-600 whitespace-nowrap">
                    <i className="ri-eye-line mr-1"></i>{formatViews(comic.views)} 阅读
                  </div>
                  <div className="text-sm text-foreground-600 whitespace-nowrap">
                    <i className="ri-book-2-line mr-1"></i>{comic.chapters} 话
                  </div>
                  <div className="text-sm text-foreground-600 whitespace-nowrap">
                    <i className="ri-calendar-line mr-1"></i>更新于 {comic.updatedAt}
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 mb-6">
                  {comic.tags.map((tag) => (
                    <span key={tag} className="px-3 py-1 rounded-full bg-background-100 text-sm text-foreground-700 whitespace-nowrap">
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="flex gap-3 mb-8">
                  <button className="px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 cursor-pointer transition-colors whitespace-nowrap">
                    <i className="ri-play-circle-line mr-1.5"></i>开始阅读
                  </button>
                  <button className="px-6 py-2.5 rounded-full bg-background-100 text-foreground-700 text-sm font-semibold hover:bg-background-200 cursor-pointer transition-colors whitespace-nowrap">
                    <i className="ri-download-line mr-1.5"></i>离线下载
                  </button>
                </div>

                <div className="mb-8">
                  <h2 className="text-base font-semibold text-foreground-950 mb-2">作品简介</h2>
                  <p className="text-sm text-foreground-600 leading-relaxed">{comic.description}</p>
                </div>

                <div>
                  <h2 className="text-base font-semibold text-foreground-950 mb-3">
                    章节列表
                    <span className="text-sm text-foreground-500 font-normal ml-2">共 {comic.chapters} 话</span>
                  </h2>
                  <div className="space-y-1 max-h-[400px] overflow-y-auto rounded-lg border border-background-200/70">
                    {comic.chapters_list.map((chapter) => (
                      <div
                        key={chapter.id}
                        className="flex items-center justify-between px-4 py-3 hover:bg-background-100 cursor-pointer transition-colors border-b border-background-100 last:border-b-0"
                      >
                        <span className="text-sm text-foreground-800 whitespace-nowrap">{chapter.title}</span>
                        <span className="text-xs text-foreground-500 whitespace-nowrap">{chapter.date}</span>
                      </div>
                    ))}
                    <div className="px-4 py-3 text-center">
                      <button className="text-sm text-primary-600 hover:text-primary-700 cursor-pointer font-medium whitespace-nowrap">
                        查看更多章节 <i className="ri-arrow-down-s-line"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {relatedComics.length > 0 && (
          <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100/60">
            <div className="max-w-7xl mx-auto">
              <h2 className="text-xl font-bold text-foreground-950 font-heading mb-6">
                <i className="ri-heart-line text-primary-500 mr-2"></i>相关推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-5" data-product-shop="">
                {relatedComics.map((rc) => (
                  <ComicCard
                    key={rc.id}
                    id={rc.id}
                    title={rc.title}
                    author={rc.author}
                    cover={rc.cover}
                    status={rc.status}
                    rating={rc.rating}
                    views={rc.views}
                    tags={rc.tags}
                    chapters={rc.chapters}
                  />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}